import "./FileDrop.css";
import { Fragment } from "react";
import { useDropzone } from "react-dropzone";
import { Trans, useTranslation } from "react-i18next";
import { Upload } from "@mui/icons-material";
import { MyFile } from "../Files/Files";
import { useToastsDispatch } from "../Toasts/ToastsContext";
import { useInputContext } from "../Form/Form";

export default function FileDrop() {
  const {
    value = [],
    onChange,
    nbFiles = 1,
    fileFormats = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/svg+xml",
    ],
    maxSizeInMB = 20, // Mega Bytes
  } = useInputContext();

  const dispatchToast = useToastsDispatch();
  const { t } = useTranslation(["common", "vault"]);

  const fileFormatsAccept = Object.fromEntries(
    fileFormats.map((fileFormat) => [fileFormat, []]),
  );

  const fileFormatsShort = {
    "application/pdf": "PDF",
    "image/jpeg": "JP(E)G",
    "image/png": "PNG",
    "image/svg+xml": "SVG",
  };

  const onDrop = (acceptedFiles, fileRejections) => {
    // onChange((prevFiles) => [...prevFiles, ...acceptedFiles]);
    onChange(acceptedFiles); // one drop (can't add new files before deleting the dropped files)

    if (fileRejections.length > 0) {
      for (const fileRejection of fileRejections) {
        const errors = fileRejection.errors;
        for (const error of errors) {
          dispatchToast({
            type: "add",
            variant: "warning",
            heading: t(`vault:common.toasts.${error.code}.heading`),
            subheading: t(`vault:common.toasts.${error.code}.subheading`),
          });
        }
      }
    }
  };

  const validator = (file) => {
    if (typeof file === File && file.name.match(/\./g).length !== 1) {
      return "wrongExtensionWarning";
    }
    return null;
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: fileFormatsAccept,
    maxFiles: nbFiles, // 0: no limit
    maxSize: maxSizeInMB * Math.pow(10, 6), // per file in bytes
    validator: validator,
    onDrop,
    noDragEventsBubbling: true,
  });

  const handleDeleteFile = (filePath) => {
    onChange((prevFiles) =>
      prevFiles.filter((prevFile) => prevFile.path !== filePath),
    );
  };

  return (
    <div className="FileDrop">
      <div {...getRootProps({ className: "FileDrop-DropZone" })}>
        <label>
          {value.length === 0 ? (
            <div className="FileDrop-DropZone-banner">
              <div className="FileDrop-DropZone-banner-icon">
                <Upload />
              </div>
              <div className="FileDrop-DropZone-banner-text">
                {t("vault:common.fileDrop.banner")}
              </div>
              {nbFiles > 1 && (
                <div className="FileDrop-DropZone-banner-subtext">
                  <Trans
                    t={t}
                    i18nKey={"vault:common.fileDrop.maxNbFiles"}
                    values={{ max: nbFiles }}
                  />
                </div>
              )}

              <div className="FileDrop-DropZone-banner-subtext">
                <Trans
                  t={t}
                  i18nKey={"vault:common.fileDrop.fileSize"}
                  values={{ maxSizeInMB: maxSizeInMB }}
                />
              </div>
              <div className="FileDrop-DropZone-banner-subtext">
                <Trans
                  t={t}
                  i18nKey={"vault:common.fileDrop.fileFormats"}
                  values={{
                    list: fileFormats
                      .map((longFormat) => fileFormatsShort[longFormat])
                      .join(" - "),
                  }}
                />
              </div>
            </div>
          ) : null}
        </label>
        <input {...getInputProps()} name="fileInput" id="fileInput" />
      </div>
      {value.length > 0 ? (
        <div className="FileDrop-FileList">
          {value.map((file) => (
            <Fragment key={file.path}>
              <MyFile
                card
                name={file.path}
                onDelete={() => handleDeleteFile(file.path)}
              />
            </Fragment>
          ))}
        </div>
      ) : null}
    </div>
  );
}
